import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtNum } from "../lib/api";
import { PageHeader, StatusBadge, Pager, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Upload, Search, Trash2, Bot, Ban } from "lucide-react";
import ImportDialog from "../components/ImportDialog";
import ResearchPanel from "../components/ResearchPanel";

const ALL = "__all";

function AddContactForm({ companyId, onAdded }) {
  const [f, setF] = useState({ first_name: "", last_name: "", title: "", email: "" });
  const [verified, setVerified] = useState(true);
  const add = async () => {
    try {
      const r = await api.post("/contacts", { company_id: companyId, ...f });
      if (verified) await api.patch(`/contacts/${r.data.id}`, { verification_status: "verified_manual" });
      toast.success("Contact ajouté");
      setF({ first_name: "", last_name: "", title: "", email: "" });
      onAdded();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="border border-slate-200 rounded p-3 space-y-2" data-testid="add-contact-form">
      <div className="eyebrow">Ajouter un contact manuellement</div>
      <div className="grid grid-cols-2 gap-2">
        {[["first_name", "Prénom"], ["last_name", "Nom"], ["title", "Fonction"], ["email", "Email"]].map(([k, l]) => (
          <Input key={k} placeholder={l} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`add-contact-${k}`} />
        ))}
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} data-testid="add-contact-verified" />Je confirme que cette adresse est valide (vérification manuelle)</label>
      <Button size="sm" disabled={!f.first_name || !f.email.includes("@")} onClick={add} data-testid="add-contact-submit">Ajouter (devient le contact principal)</Button>
    </div>
  );
}

function CompanyDrawer({ id, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const load = useCallback(() => id && api.get(`/companies/${id}`).then((r) => setC(r.data)), [id]);
  useEffect(() => { setC(null); load(); }, [load]);
  const research = async () => {
    try {
      const r = await api.post(`/research/company/${id}`);
      toast.success(`Recherche terminée : ${r.data.status}`);
      load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleDnc = async () => {
    await api.patch(`/companies/${id}`, { do_not_contact: !c.do_not_contact });
    load(); onChanged();
  };
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-xl overflow-y-auto" data-testid="company-drawer">
        {!c ? <div className="text-sm text-slate-500">Chargement…</div> : (
          <div className="space-y-6">
            <SheetHeader><SheetTitle className="font-display text-2xl text-left">{c.name}</SheetTitle></SheetHeader>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[["Domaine", c.domain], ["Pays", c.country], ["Secteur", c.industry], ["LinkedIn", c.linkedin], ["Créée", fmtDate(c.created_at)], ["Contactée", c.contacted ? fmtDate(c.last_contacted_at) : "Non"]].map(([k, v]) => (
                <div key={k}><div className="eyebrow">{k}</div><div className="truncate">{v || "—"}</div></div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={research} data-testid="company-research-btn"><Bot className="h-4 w-4 mr-1" />Rechercher le décideur</Button>
              <Button size="sm" variant="outline" onClick={toggleDnc} data-testid="company-dnc-btn"><Ban className="h-4 w-4 mr-1" />{c.do_not_contact ? "Autoriser le contact" : "Ne pas contacter"}</Button>
            </div>
            <div>
              <div className="eyebrow mb-2">Listes</div>
              <div className="text-sm">{c.lists.map((l) => l.name).join(", ") || "—"}</div>
            </div>
            <div>
              <div className="eyebrow mb-2">Contacts</div>
              {c.contacts.length === 0 ? <div className="text-sm text-slate-500">Aucun contact.</div> : c.contacts.map((ct) => (
                <div key={ct.id} className="border border-slate-200 rounded p-3 mb-2 text-sm">
                  <div className="flex justify-between"><b>{ct.first_name} {ct.last_name}</b><StatusBadge status={ct.verification_status} /></div>
                  <div className="text-slate-500">{ct.title} · {ct.email} · source {ct.source}{ct.is_primary ? " · principal" : ""}</div>
                </div>
              ))}
              <div className="mt-3"><AddContactForm companyId={id} onAdded={() => { load(); onChanged(); }} /></div>
            </div>
            <div>
              <div className="eyebrow mb-2">Historique</div>
              <div className="space-y-1 text-sm">
                {c.campaigns.map((x) => <div key={x.id}>Campagne « {x.name} » <StatusBadge status={x.status} /></div>)}
                {c.sends.map((s) => <div key={s.id} className="text-slate-600">{fmtDate(s.sent_at || s.created_at)} — {s.step === 0 ? "Email initial" : `Relance ${s.step}`} à {s.to_email} ({s.status}{s.bounced ? ", rebond" : ""})</div>)}
                {c.replies.map((r) => <div key={r.id} className="text-slate-600">{fmtDate(r.received_at)} — Réponse : <StatusBadge status={r.category} /></div>)}
                {c.campaigns.length + c.sends.length + c.replies.length === 0 && <div className="text-slate-500">Aucun historique.</div>}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function CompanyTable({ lists }) {
  const [f, setF] = useState({ q: "", list_id: ALL, research_status: ALL, contacted: ALL, country: ALL });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, countries: [] });
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    const p = { page };
    Object.entries(f).forEach(([k, v]) => { if (v && v !== ALL) p[k] = v; });
    api.get("/companies", { params: p }).then((r) => setData(r.data));
  }, [f, page]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (v) => { setPage(1); setF((x) => ({ ...x, [k]: v })); };
  const del = async (id) => {
    if (!window.confirm("Supprimer cette entreprise et ses contacts ?")) return;
    await api.delete(`/companies/${id}`);
    toast.success("Entreprise supprimée");
    load();
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative w-72">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
          <Input className="pl-9" placeholder="Nom ou domaine…" value={f.q} onChange={(e) => set("q")(e.target.value)} data-testid="companies-search-input" />
        </div>
        <Select value={f.list_id} onValueChange={set("list_id")}>
          <SelectTrigger className="w-52" data-testid="companies-list-filter"><SelectValue placeholder="Liste" /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Toutes les listes</SelectItem>{lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.research_status} onValueChange={set("research_status")}>
          <SelectTrigger className="w-48" data-testid="companies-research-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous statuts recherche</SelectItem>
            {["pending", "found", "found_unverified", "not_found", "error"].map((s) => <SelectItem key={s} value={s}><StatusBadge status={s} /></SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={f.contacted} onValueChange={set("contacted")}>
          <SelectTrigger className="w-44" data-testid="companies-contacted-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Contactées ou non</SelectItem><SelectItem value="true">Déjà contactées</SelectItem><SelectItem value="false">Jamais contactées</SelectItem></SelectContent>
        </Select>
        <Select value={f.country} onValueChange={set("country")}>
          <SelectTrigger className="w-44" data-testid="companies-country-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tous pays</SelectItem>{data.countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {data.total === 0 ? <Empty title="Aucune entreprise">Importez un fichier CSV pour commencer.</Empty> : (
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="companies-table">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr><th className="px-4 py-2.5 font-medium">Entreprise</th><th className="px-4 font-medium">Domaine</th><th className="px-4 font-medium">Pays</th><th className="px-4 font-medium">Secteur</th><th className="px-4 font-medium">Décideur</th><th className="px-4 font-medium">Recherche</th><th className="px-4 font-medium">Contactée</th><th /></tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 row-hover cursor-pointer" onClick={() => setOpen(c.id)} data-testid={`company-row-${c.id}`}>
                  <td className="px-4 py-2.5 font-medium">{c.name}{c.do_not_contact && <span className="ml-2 text-[10px] text-red-600">NE PAS CONTACTER</span>}</td>
                  <td className="px-4 text-slate-600">{c.domain || "—"}</td>
                  <td className="px-4 text-slate-600">{c.country || "—"}</td>
                  <td className="px-4 text-slate-600 truncate max-w-[160px]">{c.industry || "—"}</td>
                  <td className="px-4">{c.contact ? <span>{c.contact.first_name} {c.contact.last_name} <span className="text-slate-400">· {c.contact.title}</span></span> : "—"}</td>
                  <td className="px-4"><StatusBadge status={c.research_status} /></td>
                  <td className="px-4 text-slate-600">{c.contacted ? "Oui" : "Non"}</td>
                  <td className="px-2"><button onClick={(e) => { e.stopPropagation(); del(c.id); }} className="text-slate-400 hover:text-red-600" data-testid={`company-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pager page={page} total={data.total} onChange={setPage} />
      <CompanyDrawer id={open} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}

function ListsTab({ lists, reload }) {
  const [name, setName] = useState("");
  const create = async () => {
    if (!name.trim()) return;
    await api.post("/lists", { name });
    setName(""); reload(); toast.success("Liste créée");
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer la liste ? Les entreprises sont conservées.")) return;
    await api.delete(`/lists/${id}`); reload();
  };
  return (
    <div className="space-y-4">
      <div className="flex gap-2 max-w-md">
        <Input placeholder="Nom de la nouvelle liste" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-list-input" />
        <Button onClick={create} data-testid="create-list-btn">Créer</Button>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {lists.map((l) => (
          <div key={l.id} className="border border-slate-200 rounded-md p-5" data-testid={`list-card-${l.id}`}>
            <div className="flex justify-between"><div className="font-medium">{l.name}</div><button onClick={() => del(l.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></div>
            <div className="text-xs text-slate-500 mt-1">{l.description}</div>
            <div className="flex gap-6 mt-4 text-sm"><div><div className="eyebrow">Entreprises</div><div className="tabular">{fmtNum(l.companies)}</div></div><div><div className="eyebrow">Avec décideur</div><div className="tabular">{fmtNum(l.with_contact)}</div></div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImportsTab({ refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const load = () => api.get("/imports").then((r) => setItems(r.data));
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [refreshKey]);
  if (!items.length) return <Empty title="Aucun import">L'historique des imports CSV apparaîtra ici.</Empty>;
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden">
      <table className="w-full text-sm" data-testid="imports-table">
        <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr>{["Date", "Fichier", "Liste", "Statut", "Lignes", "Acceptées", "Doublons", "Ignorées", "Déjà contactées", "Exclues"].map((h) => <th key={h} className="px-4 py-2.5 font-medium">{h}</th>)}</tr></thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.id} className="border-t border-slate-100">
              <td className="px-4 py-2.5">{fmtDate(i.created_at)}</td><td className="px-4">{i.filename}</td><td className="px-4">{i.list_name}</td>
              <td className="px-4"><StatusBadge status={i.status} /></td>
              {["total", "accepted", "duplicates", "ignored", "already_contacted", "excluded"].map((k) => <td key={k} className="px-4 tabular">{fmtNum(i.stats?.[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Companies() {
  const [lists, setLists] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [key, setKey] = useState(0);
  const [tab, setTab] = useState("companies");
  const loadLists = useCallback(() => api.get("/lists").then((r) => setLists(r.data)), []);
  useEffect(() => { loadLists(); }, [loadLists, key]);
  return (
    <div>
      <PageHeader eyebrow="Base de prospection" title="Entreprises" description="Import CSV, normalisation, dédoublonnage et recherche automatique des décideurs."
        actions={<Button onClick={() => setImportOpen(true)} data-testid="open-import-btn"><Upload className="h-4 w-4 mr-2" />Importer un CSV</Button>} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="companies" data-testid="tab-companies">Entreprises</TabsTrigger>
          <TabsTrigger value="research" data-testid="tab-research">Agent de recherche</TabsTrigger>
          <TabsTrigger value="lists" data-testid="tab-lists">Listes</TabsTrigger>
          <TabsTrigger value="imports" data-testid="tab-imports">Historique des imports</TabsTrigger>
        </TabsList>
        <TabsContent value="companies"><CompanyTable key={key} lists={lists} /></TabsContent>
        <TabsContent value="research"><ResearchPanel lists={lists} /></TabsContent>
        <TabsContent value="lists"><ListsTab lists={lists} reload={loadLists} /></TabsContent>
        <TabsContent value="imports"><ImportsTab refreshKey={key} /></TabsContent>
      </Tabs>
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} lists={lists} onDone={() => { setKey((k) => k + 1); setTab("imports"); }} />
    </div>
  );
}
